import React from 'react';
import { SequenceMissionProps } from '../../../types/mission';
import styles from './SequenceMissionBriefing.module.css';
import { getTacticalThemeDescription } from '../../../data';
import ChessBoard from '../../chess/ChessBoard';
import { Chess } from 'chess.js';
import ChessService from '../../../services/chess/chessService';
import { formatSkillLabel } from '../../../utils/missionDisplay';

/**
 * Props for the SequenceMissionBriefing component
 */
interface SequenceMissionBriefingProps {
  mission: SequenceMissionProps;
  onStartMission: () => void;
  onBack: () => void;
}

/**
 * SequenceMissionBriefing component
 * 
 * Displays the briefing for a sequence mission, including tactical theme information
 * and starting position visualization.
 */
const SequenceMissionBriefing: React.FC<SequenceMissionBriefingProps> = ({
  mission,
  onStartMission,
  onBack,
}) => {
  // --- Helper functions ---

  /**
   * Render difficulty display
   */
  const renderDifficulty = () => {
    return (
      <div className={styles.difficultyBadge}>
        Level {mission.difficulty} Sequence Mission
      </div>
    );
  };

  /**
   * Render tactical theme information
   */
  const renderTacticalTheme = () => {
    const themeDescription = getTacticalThemeDescription(mission.tacticalTheme);
    
    return (
      <div className={styles.tacticalThemeSection}>
        <h2 className={styles.sectionTitle}>Tactical Theme: {mission.tacticalTheme.charAt(0).toUpperCase() + mission.tacticalTheme.slice(1)}</h2>
        <p className={styles.themeDescription}>{themeDescription}</p>
        {mission.secondaryThemes && mission.secondaryThemes.length > 0 && (
          <div className={styles.secondaryThemes}>
            <h3>Related Themes:</h3>
            <div className={styles.themeList}>
              {mission.secondaryThemes.map(theme => (
                <span key={theme} className={styles.themeTag}>
                  {theme.charAt(0).toUpperCase() + theme.slice(1)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render rewards section (XP and skill points)
   */
  const renderRewards = () => (
    <div className={styles.rewardsSection}>
      <h2 className={styles.sectionTitle}>Rewards</h2>
      <div className={styles.rewardsGrid}>
        <div className={styles.rewardItem}>
          <span className={styles.rewardLabel}>Experience:</span>
          <span className={styles.rewardValue}>{mission.rewards.experience} XP</span>
        </div>
        {Object.entries(mission.rewards.skillPoints).map(([skill, points]) => (
          points > 0 && (
            <div key={skill} className={styles.rewardItem}>
              <span className={styles.rewardLabel}>
                {formatSkillLabel(skill)}:
              </span>
              <span className={styles.rewardValue}>+{points} points</span>
            </div>
          )
        ))}
      </div>
    </div>
  );

  /**
   * Create and manage a chess service instance for this component
   */
  const chessService = React.useMemo(() => {
    const chess = new Chess(mission.position);
    return ChessService.create(chess);
  }, [mission.position]);

  // --- Main component render ---

  return (
    <div className={styles.container} data-testid="sequence-mission-briefing">
      <div className={styles.header}>
        {/* @TODO: Add asset: Top-secret or classified stamp image should be added here */}
        <h1 className={styles.title}>{mission.title}</h1>
        {renderDifficulty()} 
      </div>

      <div className={styles.content}>
        <div className={styles.briefingSection}>
          <h2 className={styles.sectionTitle}>Mission Briefing</h2>
          <p className={styles.briefingText}>{mission.briefing}</p>
        </div>

        <div className={styles.positionVisualization}>
          <h2 className={styles.sectionTitle}>Starting Position</h2>
          <div className={styles.boardContainer}>
            <ChessBoard 
              fen={mission.position} 
              chessService={chessService}
              allowMoves={false}
              size={300}
            />
          </div>
          <p className={styles.positionHint}>
            Memorize this setup, agent. Your mission requires precise execution of tactical maneuvers.
          </p>
        </div>

        {renderTacticalTheme()}

        <div className={styles.objectiveSection}>
          <h2 className={styles.sectionTitle}>Objective</h2>
          <p className={styles.objectiveText}>{mission.objective}</p>
        </div>

        <div className={styles.successSection}>
          <h2 className={styles.sectionTitle}>Success Criteria</h2>
          <p className={styles.successText}>{mission.successCriteria}</p>
        </div>
        
        {mission.timeLimit && (
          <div className={styles.timeLimitSection}>
            <h2 className={styles.sectionTitle}>Time Limit</h2>
            <p className={styles.timeLimitText}>
              {mission.timeLimit} seconds
            </p>
          </div>
        )}

        {renderRewards()}
      </div>

      <div className={styles.acceptSection}>
        <p className={styles.warningText}>
          This tactical operation has been classified as Level {mission.difficulty}. You are authorized to proceed when ready.
        </p>
        <div className={styles.actionButtons}>
          <button 
            className={styles.startButton}
            onClick={onStartMission}
            aria-label="Accept mission and begin"
            data-testid="start-mission-button"
          >
            Accept Mission
          </button>
          <button 
            className={styles.backButton}
            onClick={onBack}
            aria-label="Return to previous screen"
            data-testid="back-button"
          >
            Return to HQ
          </button>
        </div>
      </div>
    </div>
  );
};

export default SequenceMissionBriefing; 
